import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { gameKey } from '@/features/game/useGame'
import { model } from '@/shared/bot/net/levels'
import type { GameView } from '@/shared/game/types'
import { localBotStep } from '@/shared/local/store'

/**
 * Ход компьютера в партии без сервера.
 *
 * В сетевой партии этим занята очередь: джоба с задержкой (App\Jobs\PlayBotTurn),
 * и паузы там нужны, чтобы ходы не приходили быстрее, чем доска успевает их
 * показать. Здесь очереди нет — есть таймер, и он в лучшем положении:
 * ему видно, доиграла ли доска предыдущую сцену, поэтому пауза добавляется
 * к анимации, а не гадает про неё.
 */

/** Сколько «думать» перед броском. Кубики бросают быстрее, чем ходят. */
const PAUSE_ROLL = 700

/** ...и перед ходом: выбор фишки человеку даётся дольше броска. */
const PAUSE_MOVE = 900

export function useLocalBot(gameId: string, view: GameView | undefined, busy: boolean): void {
  const client = useQueryClient()

  const level = view?.players.find((player) => player.color === view.currentColor)?.bot ?? null
  const turn = view?.local === true && view.status === 'playing' && level !== null
  const awaiting = view?.turn?.awaiting === true
  // Номер последнего события: по нему таймер перезаводится после каждого
  // действия, включая ходы человека.
  const last = view?.feed[view.feed.length - 1]?.seq ?? 0

  /*
   * last в теле не читается, но именно он перезаводит таймер после каждого
   * действия: после хода компьютера остальные признаки те же самые, и без
   * него он замер бы, не доиграв свой ход.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: см. выше
  useEffect(() => {
    // Пока доска доигрывает предыдущую сцену, компьютер ждёт: ходить поверх
    // собственной анимации — ровно то, из-за чего партия против него
    // выглядела как чересполосица.
    if (!turn || busy) return

    // Модель грузится один раз за сессию; пока она едет, таймер мог быть уже
    // снят (ход сменился, экран закрыли) — тогда шаг не делается.
    let live = true
    const timer = setTimeout(
      () => {
        void model().then((net) => {
          if (live) client.setQueryData(gameKey(gameId), localBotStep(gameId, net))
        })
      },
      awaiting ? PAUSE_ROLL : PAUSE_MOVE,
    )

    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [turn, busy, awaiting, last, gameId, client])
}
